import Swal from 'sweetalert2';
import { Subject, takeUntil } from 'rxjs';
import { Component } from '@angular/core';
import { BooleanInput } from 'ng-zorro-antd/core/types';
import { IPreviewDraft } from 'src/app/interfaces/Detailed/IPreviewDraftDetailed';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { AllergiesExpandComponent } from '../components/alerts-section/allergies/allergies-expand/allergies-expand.component';
import { ProcedureExpandComponent } from '../components/medical-problems-section/procedure/procedure-expand/procedure-expand.component';
import { MedicalDeviceExpandComponent } from '../components/medical-problems-section/medical-device/medical-device-expand/medical-device-expand.component';
import { CurrentProblemsExpandComponent } from '../components/medical-problems-section/current-problems/current-problems-expand/current-problems-expand.component';
import { Router } from '@angular/router';
import { NzSelectOptionInterface } from 'ng-zorro-antd/select';
import {
  IDynamicItem,
  IDynamicTable,
} from 'src/app/interfaces/DynamicTable/IDynamicDetailed';
import { IQuestionnaireDetailed } from 'src/app/interfaces/Questionnaire/IQuestionnaireDetailed';
import { DynamicQuestionnaireService } from 'src/app/services/dynamicQuestionnaire.service';
import { TranslateService } from '@ngx-translate/core';
import { COLORS } from 'src/styles/colors';
import { SwalToastService } from 'src/app/services/swal.service';

@Component({
    selector: 'app-preview-summary',
    templateUrl: './preview-summary.component.html',
    styleUrls: ['./preview-summary.component.css'],
    standalone: false
})
export class PreviewSummaryComponent {
  public allergiesExpandComp = AllergiesExpandComponent;
  public procedureExpandComp = ProcedureExpandComponent;
  public medicalDeviceExpandComp = MedicalDeviceExpandComponent;
  public problemExpandComp = CurrentProblemsExpandComponent;

  private destroy$ = new Subject<void>();

  data?: IPreviewDraft;

  switchValue?: boolean = false;

  pdValue?: number;

  pdList: NzSelectOptionInterface[] = [];

  noteValue?: string = '';

  isLoading: boolean = false;
  isPdLoading: BooleanInput = false;

  questionnaireData: any[] = [];

  questionnaireSections: Array<{
    questionnaireId: number;
    questionnaireTemplateId: number;
    title: string;
    rows: any[];
  }> = [];

  constructor(
    private readonly eocService: EpisodeOfCareService,
    private readonly questionnaireService: DynamicQuestionnaireService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  handleEndedValue(value: boolean) {
    this.eocService.eocEndedSub.next(value);
  }

  setPD(id: number) {
    this.eocService.setPD(id);
  }

  setNote(note: any) {
    this.eocService.setNote(note);
  }

  isInpatientView() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  ngOnInit() {
    this.isLoading = true;
    // this.getQuestionnaireData().then((data) => {
    //   this.questionnaireData = data;
      
    // });
    this.eocService.unvalidatePreview();
    this.eocService.unsetNote();
    this.eocService.unsetPd();
    this.eocService.eocEndedSub.next(false);

    this.eocService.potentialDiagnosisSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: number | undefined) => {
        this.pdValue = data || undefined;
      });

    this.isLoading = true;
    this.isPdLoading = true;
    this.eocService
      .getPreviewDraft(this.eocService.currentVisitId())
      ?.subscribe({
        next: async (d) => {
          this.data = d?.data;
          this.isLoading = false;

          if (d?.data?.getPotentialDiagnosis?.id) {
            this.eocService.setPD(d?.data?.getPotentialDiagnosis?.id);
          }

          if (d?.data?.isEmpty == false) {
            this.eocService.validatePreview();
          }

          await this.loadAllQuestionnaires();
          this.isLoading = false; // Update loading status
        },
        error: (err) => {
          this.swalToastService.toastError('Unable to retrieve Draft Records');
        },
      });

    this.eocService.getPotentialDiagnosis().subscribe({
      next: (d) => {
        this.pdList = d?.data?.map((d) => {
          return {
            label: d.name,
            value: d.id,
          };
        });
        this.isPdLoading = false;
      },
      error: (err) => {
        this.swalToastService.toastError(
          'Unable to retrieve Potential Diagnosis'
        );
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getLaboratoryData(
    laboratoryData: IDynamicTable<IDynamicItem>[],
    groupId: number
  ) {
    return laboratoryData.filter((dataItem) => dataItem.groupId == groupId);
  }

  private async loadAllQuestionnaires(): Promise<void> {
    const questionnaires = this.data?.getAllQuestionnaires ?? [];
    if (!questionnaires.length) {
      this.questionnaireSections = [];
      return;
    }

    // Keep spinner on while we load questionnaire details
    this.isLoading = true;

    // Keep latest questionnaire per templateId using MAX(id)
    const latestByTemplate = new Map<number, IQuestionnaireDetailed>();

    for (const q of questionnaires) {
      const existing = latestByTemplate.get(q.questionnaireTemplateId);

      if (!existing || q.id > existing.id) {
        latestByTemplate.set(q.questionnaireTemplateId, q);
      }
    }

    const uniqueQuestionnaires = Array.from(latestByTemplate.values());

    try {
      const sections = await Promise.all(
        uniqueQuestionnaires.map(async (q) => {
          const rows = await this.getQuestionnaireData(q.questionnaireTemplateId);
          return {
            questionnaireId: q.id,
            questionnaireTemplateId: q.questionnaireTemplateId,
            title: rows.at(0).title,
            rows,
          };
        })
      );

      this.questionnaireSections = sections.filter((s) => s.rows.length > 0);
    } catch (e) {
      this.questionnaireSections = [];
      this.swalToastService.toastError('Unable to retrieve Questionnaire Data');
    } finally {
      this.isLoading = false;
    }
  }

  getQuestionnaireData(templateId: number): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.questionnaireService
        .getQuestionnaireData(templateId) // questionnaire template id (inpatient = 1, outpatient = 2)
        .subscribe(
          (data: IQuestionnaireDetailed) => {
            if (data && data.questionnaire && data.answers) {
              const questionnaire = data.questionnaire;
              const answers = data.answers.answer;              
              // Total number of pages
              const totalNumberOfPages = questionnaire.pages.length;

              // Total number of questions
              let totalNumberOfQuestions = questionnaire.pages.reduce(
                (total, page) => total + page.elements.length,
                0
              );

              // Number of questions completed
              let questionsCompleted = answers.length;
              
              // Fast Protocol adjust number of totals due to different questions
              if (templateId === 2) {
                const fast = this.computeFastTotals(answers);
                totalNumberOfQuestions = fast.totalQuestions;
                questionsCompleted = fast.completed;
              }
              // Number of questions remaining
              const questionsRemaining =
                totalNumberOfQuestions - questionsCompleted;

              // Completion percentage
              const completionPercentage =
                ((questionsCompleted / totalNumberOfQuestions) * 100).toFixed(
                  2
                ) + '%';

              // Determine the status
              const status = data.isSubmitted
                ? 'Submitted'
                : 'Not Submitted (Ready for Submission)';

              // Create the data object
              const questionaireDataDto = [
                {
                  dateTime: data.creationDate || '',
                  title: questionnaire.title,
                  totalNumberOfPages: `${totalNumberOfPages} pages`,
                  totalNumberOfQuestions: `${totalNumberOfQuestions} questions`,
                  completionPercentage: `${Math.round(
                    parseFloat(completionPercentage)
                  )}% completed`,
                  questionsCompleted: `${questionsCompleted} out of ${totalNumberOfQuestions} questions completed`,
                  questionsRemaining: `${questionsRemaining} questions remaining`,
                  status,
                },
              ];

              // Resolve the promise with the data
              resolve(questionaireDataDto);
            } else {
              resolve([]); // Resolve with an empty array if there's no data
            }
          },
          (error) => {
            reject(error); // Reject the promise in case of an error
          }
        );
    });
  }

  private computeFastTotals(answers: any[]): {
    totalQuestions: number;
    completed: number;
  } {
    const answeredIds = new Set<number>(
      answers.map((a) => a.questionTemplateId)
    );

    // Always answerable: Time (question45)
    let answerable = 1;

    // Face: q40 (normal) OR q41 (abnormal)
    if (answeredIds.has(40) || answeredIds.has(41)) answerable += 1;
    if (answeredIds.has(41)) answerable += 1; // side q47

    // Arm: q42 OR q43
    if (answeredIds.has(42) || answeredIds.has(43)) answerable += 1;
    if (answeredIds.has(43)) answerable += 1; // side q48

    // Speech: q44 OR q46
    if (answeredIds.has(44) || answeredIds.has(46)) answerable += 1;

    return {
      totalQuestions: answerable,
      completed: Math.min(answeredIds.size, answerable),
    };
  }

}
